import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  CreateOrganizationAction,
  CreateOrganizationPayload,
} from "../../Redux/Actions/OrganizationAction";
import { CREATE_ORGANIZATION_RESET } from "../../Constants/OrganizationConstant";

const ORG_TYPE_OPTIONS = [
  { value: "ORGANIZATION", label: "Organization" },
  { value: "UNIVERSITY", label: "University" },
];
const ORG_NAME_MIN = 2;
const ORG_NAME_MAX = 100;
const FULLNAME_MIN = 2;
const FULLNAME_MAX = 150;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const STAFF_EMAIL_REGEX = /^[^\s@]+@(skillbrew\.com|skillbrew\.ai)$/i;
const HTTP_CONFLICT = 409;

interface CreateOrganizationDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

interface SameDomainConflict {
  existing_org_name: string;
  existing_owner_email: string;
}

interface CreatedOrganization {
  org_id: number;
  onboarding_status: string;
  owner: { email: string; is_existing_user: boolean };
  account_manager: { id: number; email: string };
  temporary_password?: string;
  temp_password_expires_at?: string;
}

const EMPTY_FORM = {
  org_name: "",
  org_type: "ORGANIZATION",
  owner_email: "",
  owner_fullname: "",
  account_manager_email: "",
};

const CreateOrganizationDialog = ({
  open,
  onClose,
  onCreated,
}: CreateOrganizationDialogProps) => {
  const dispatch = useDispatch<any>();
  const createState = useSelector((state: any) => state.CreateOrganizationData);
  const [form, setForm] = useState(EMPTY_FORM);
  const [conflict, setConflict] = useState<SameDomainConflict | null>(null);
  const [created, setCreated] = useState<CreatedOrganization | null>(null);

  const orgName = form.org_name.trim();
  const ownerEmail = form.owner_email.trim();
  const ownerFullname = form.owner_fullname.trim();
  const accountManagerEmail = form.account_manager_email.trim();

  const orgNameValid =
    orgName.length >= ORG_NAME_MIN && orgName.length <= ORG_NAME_MAX;
  const ownerEmailValid = EMAIL_REGEX.test(ownerEmail);
  const ownerFullnameValid =
    ownerFullname.length >= FULLNAME_MIN &&
    ownerFullname.length <= FULLNAME_MAX;
  const accountManagerValid =
    accountManagerEmail === "" || STAFF_EMAIL_REGEX.test(accountManagerEmail);
  const formValid =
    orgNameValid && ownerEmailValid && ownerFullnameValid && accountManagerValid;
  const loading = Boolean(createState?.loading);

  const setField =
    (field: keyof typeof EMPTY_FORM) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
      setConflict(null);
      if (createState?.error) dispatch({ type: CREATE_ORGANIZATION_RESET });
    };

  const handleClose = () => {
    if (loading) return;
    const wasCreated = created !== null;
    setForm(EMPTY_FORM);
    setConflict(null);
    setCreated(null);
    dispatch({ type: CREATE_ORGANIZATION_RESET });
    onClose();
    if (wasCreated) onCreated();
  };

  const submit = async (confirmSameDomain: boolean) => {
    if (!formValid || loading) return;
    const payload: CreateOrganizationPayload = {
      org_name: orgName,
      org_type: form.org_type,
      owner_email: ownerEmail,
      owner_fullname: ownerFullname,
      confirm_same_domain: confirmSameDomain,
    };
    if (accountManagerEmail) {
      payload.account_manager_email = accountManagerEmail;
    }
    const response: any = await dispatch(CreateOrganizationAction(payload));

    if (response?.status === true) {
      setConflict(null);
      setCreated(response.data);
    } else if (response?.status_code === HTTP_CONFLICT && response?.data) {
      setConflict(response.data);
    }
  };

  const copyPassword = async () => {
    if (!created?.temporary_password) return;
    try {
      await navigator.clipboard.writeText(created.temporary_password);
      toast.success("Temporary password copied.");
    } catch {
      toast.error("Could not copy. Select the password and copy it manually.");
    }
  };

  const errorMessage =
    !conflict && !created && createState?.error ? createState.error : null;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {created ? "Organization Created" : "Create Organization"}
      </DialogTitle>

      {created ? (
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="success">
              The organization was created and is waiting for the owner to
              complete onboarding.
            </Alert>
            <Typography variant="body2">
              <b>Owner:</b> {created.owner.email}
              {created.owner.is_existing_user ? " (existing user)" : ""}
            </Typography>
            <Typography variant="body2">
              <b>Account manager:</b> {created.account_manager.email}
            </Typography>
            <Typography variant="body2">
              <b>Status:</b> {created.onboarding_status}
            </Typography>
            {created.temporary_password ? (
              <>
                <TextField
                  label="Temporary password"
                  value={created.temporary_password}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
                <Typography variant="caption" color="text.secondary">
                  This password is shown only once. A welcome email with it is
                  sent to the owner.
                  {created.temp_password_expires_at
                    ? ` It expires on ${new Date(
                        created.temp_password_expires_at
                      ).toLocaleString()}.`
                    : ""}
                </Typography>
                <Button onClick={copyPassword} variant="outlined">
                  Copy password
                </Button>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                The owner already has an account, so no new password was
                generated.
              </Typography>
            )}
          </Stack>
        </DialogContent>
      ) : (
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Organization Name"
              value={form.org_name}
              onChange={setField("org_name")}
              fullWidth
              autoFocus
              required
              error={form.org_name.length > 0 && !orgNameValid}
              helperText={
                form.org_name.length > 0 && !orgNameValid
                  ? `Must be ${ORG_NAME_MIN}-${ORG_NAME_MAX} characters.`
                  : ""
              }
            />
            <TextField
              select
              label="Organization Type"
              value={form.org_type}
              onChange={setField("org_type")}
              fullWidth
              required
            >
              {ORG_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Owner Full Name"
              value={form.owner_fullname}
              onChange={setField("owner_fullname")}
              fullWidth
              required
              error={form.owner_fullname.length > 0 && !ownerFullnameValid}
              helperText={
                form.owner_fullname.length > 0 && !ownerFullnameValid
                  ? `Must be ${FULLNAME_MIN}-${FULLNAME_MAX} characters.`
                  : ""
              }
            />
            <TextField
              label="Owner Work Email"
              value={form.owner_email}
              onChange={setField("owner_email")}
              fullWidth
              required
              error={form.owner_email.length > 0 && !ownerEmailValid}
              helperText={
                form.owner_email.length > 0 && !ownerEmailValid
                  ? "Enter a valid email address."
                  : "Personal domains such as gmail.com are not allowed."
              }
            />
            <TextField
              label="Account Manager Email (optional)"
              value={form.account_manager_email}
              onChange={setField("account_manager_email")}
              fullWidth
              error={form.account_manager_email.length > 0 && !accountManagerValid}
              helperText={
                form.account_manager_email.length > 0 && !accountManagerValid
                  ? "Enter a valid @skillbrew.com or @skillbrew.ai email address."
                  : "Leave empty to assign yourself."
              }
            />
            {conflict && (
              <Alert severity="warning">
                An organization already exists for this email domain:{" "}
                <b>{conflict.existing_org_name}</b> (owner{" "}
                {conflict.existing_owner_email}). Create another one anyway?
              </Alert>
            )}
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          </Stack>
        </DialogContent>
      )}

      <DialogActions>
        {created ? (
          <Button onClick={handleClose} variant="contained">
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} variant="outlined" disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={() => submit(conflict !== null)}
              variant="contained"
              disabled={!formValid || loading}
            >
              {loading
                ? "Creating..."
                : conflict
                ? "Create anyway"
                : "Create"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateOrganizationDialog;
